/**
 * Game utility functions
 */

import type { Board, Player } from './types.ts';
import { WINNING_COMBINATIONS } from './types.ts';

/**
 * Check if a player has won the game
 */
export function checkWinner(board: Board): Player | null {
  for (const combination of WINNING_COMBINATIONS) {
    const [a, b, c] = combination;
    if (board[a] && board[a] === board[b] && board[a] === board[c]) {
      return board[a] as Player;
    }
  }
  return null;
}

/**
 * Check if the board is full (draw condition)
 */
export function isBoardFull(board: Board): boolean {
  return board.every((cell): cell is Player => cell !== null);
}

/**
 * Check if a move is valid
 */
export function isValidMove(board: Board, position: number): boolean {
  return position >= 0 && position < 9 && board[position] === null;
}

/**
 * Get the opponent player
 */
export function getOpponent(player: Player): Player {
  return player === 'X' ? 'O' : 'X';
}

/**
 * Create an empty board
 */
export function createEmptyBoard(): Board {
  return Array(9).fill(null) as Board;
}

/**
 * Make a move on the board
 */
export function makeMove(
  board: Board,
  position: number,
  player: Player
): Board {
  if (!isValidMove(board, position)) {
    throw new Error('Invalid move');
  }

  const newBoard = [...board] as Board;
  newBoard[position] = player;
  return newBoard;
}
